import React, { useState } from "react";
import {
  Box,
  Button,
  Fade,
  Grid2,
  IconButton,
  Skeleton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "../../../app/helpers";
import { RootState } from "../../../app/rootReducer";
import {
  setFilterSelected,
  setSelectedBookingType,
  setSelectedLocation
} from "../../../features/Reports/slices/report.slice";
import { fetchEntities, fetchReport } from "../../../features/Reports/thunks/report.thunk";
import { AreaChartsReport } from "./area-chart.component";
import CapacityPerHourReport from "./capacity-per-hour-report.component";
import { BookingType } from "../../../features/Booking-Form/typings/booking-inputs";
import { initialWeekdays } from "../../../features/Reports/initialWeekdays.constant";
import { LocationReportPopper } from "./LocationReportPopper";
import { buildCapacityReportAvailableTypes } from "../CapacityFilter/DateAndTypeSelector/capacity-report-available-types";

/**
 * @description Component that renders the user menu dropdown in the right top corner.
 * @version 0.1.0
 */

type P = {
  name: string;
  id: number;
  mode: boolean;
};

const LocationCapacityMarker: React.FC<P> = ({ name, id, mode }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const {
    userInformation: { sub, placeTypes, zoneTypes }
  } = useSelector((state: RootState) => state.login);
  const { workplaceBookingEnabled } = useSelector(
    (state: RootState) => state.login.userInformation.company.meta
  );
  const {
    selectedLocation,
    selectedTimeframe,
    capacitySelectedLocations,
    selectableEntities,
    calculateWithoutCosts,
    zoneRestrictions
  } = useSelector((state: RootState) => state.report);
  const dispatch = useDispatch();

  const selectedLocationData = capacitySelectedLocations?.find(
    location => location.id === selectedLocation.id
  )?.capacity;

  const [open, setOpen] = useState(false);

  const handlePopperOpen = () => {
    setOpen(true);
  };
  const handlePopperClose = () => {
    setOpen(false);
  };

  const handleDetailReportOpen = () => {
    if (!selectedTimeframe) return;

    const availableTypes = buildCapacityReportAvailableTypes(
      placeTypes,
      zoneTypes,
      workplaceBookingEnabled
    );

    // close the location popper report
    handlePopperClose();

    // select the initial set-up for detail report and open the detail dialog
    dispatch(setSelectedBookingType(availableTypes[0].name as BookingType));
    dispatch(
      setFilterSelected({
        selectedDepartmentIds: [],
        selectedWeekdays: initialWeekdays,
        selectedAttributes: []
      })
    );
    dispatch(setSelectedLocation({ id: id, open: false, sub: sub }));
    dispatch(
      fetchEntities({
        id: id,
        sub: sub,
        cost: calculateWithoutCosts,
        start: selectedTimeframe.timeframeStart,
        end: selectedTimeframe.timeframeEnd,
        bookingType: availableTypes[0].name as BookingType,
        firstFloor: selectableEntities?.find(location => location.id === selectedLocation.id)
          ?.floors[0]?.inventoryId,
        weekdays: initialWeekdays
      })
    );
  };

  return (
    <Grid2
      sx={{ cursor: "hover" }}
      container
      direction={"column"}
      justifyContent={"center"}
      alignItems={"center"}
    >
      {selectedTimeframe && (
        <div>
          <Tooltip title={<Typography>{name}</Typography>} aria-label={name}>
            <Box
              data-testid="location-capa-marker-typo-box"
              onClick={() => {
                handlePopperOpen();
                dispatch(setSelectedLocation({ id: id, open: true, sub: sub }));
                dispatch(
                  fetchReport({
                    id: id,
                    sub: sub,
                    cost: calculateWithoutCosts,
                    start: selectedTimeframe.timeframeStart,
                    end: selectedTimeframe.timeframeEnd,
                    filterType: "location",
                    status: true,
                    update: true,
                    zoneBooking: mode,
                    companyId: mode ? zoneRestrictions?.company?.id : undefined,
                    zoneBookingObject: mode
                      ? zoneRestrictions?.object?.zoneBookingObject
                      : undefined
                  })
                );
              }}
              sx={{
                /**
                 * previously used getColor(averageCapacity), but currently don't make a data request on the google map
                 * so, for now, use company color on the marker
                 */
                background: theme.palette.primary.main,
                textDecoration: "none",
                borderRadius: "100px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                border: "2px solid white",
                width: 30,
                height: 30,
                cursor: "pointer",
                color: "white !important",
                boxShadow: "10px 10px 20px -10px rgba(0,0,0,0.75)",
                "&:hover": {
                  background: "#131421 !important"
                }
              }}
            >
              <Typography data-testid="location-capa-marker-typo">{name.slice(0, 1)}</Typography>
            </Box>
          </Tooltip>
        </div>
      )}

      <LocationReportPopper
        id={selectedLocation.id}
        open={open}
        onClose={() => {
          dispatch(setSelectedLocation({ id: id, open: false, sub: sub }));
          handlePopperClose();
        }}
      >
        <Fade mountOnEnter unmountOnExit in={!!selectedTimeframe}>
          <Grid2
            container
            sx={{ maxWidth: 400 }}
            justifyContent={"center"}
            data-testid="context-popper-report-parent-grid"
          >
            {selectedLocationData ? (
              <>
                {/* header of popper report */}
                <>
                  <Grid2 size={"grow"} />
                  <Grid2 size={{ xs: 9 }} sx={{ alignSelf: "center" }}>
                    <Typography align={"center"} lineHeight={2.5}>
                      {t("Users visited")}: {selectedLocationData.distinctTotalUsers?.length ?? 0}
                    </Typography>
                  </Grid2>
                  <Grid2 size={"grow"} sx={{ textAlign: "right" }}>
                    <IconButton
                      sx={{ float: "right" }}
                      data-testid="location-popper-report-close"
                      onClick={() => {
                        dispatch(setSelectedLocation({ id: id, open: false, sub: sub }));
                        handlePopperClose();
                      }}
                      size="large"
                    >
                      <Close />
                    </IconButton>
                  </Grid2>
                </>

                {/* contents of popper report */}
                <>
                  <AreaChartsReport
                    width={isMobile ? "90%" : "45vw"}
                    height="265px"
                    capacityObjects={[
                      { id: selectedLocation.id, data: selectedLocationData, label: name }
                    ]}
                  />
                  <CapacityPerHourReport
                    width={isMobile ? "90%" : "45vw"}
                    height="265px"
                    capacityObjects={[
                      { id: selectedLocation.id, data: selectedLocationData, label: name }
                    ]}
                  />

                  <Button
                    data-testid="loc-capa-marker-get-more-details-btn"
                    onClick={handleDetailReportOpen}
                  >
                    {t("Get more details")}
                  </Button>
                </>
              </>
            ) : (
              <Box
                sx={[{ height: 660 }, isMobile ? { width: "calc(100vw - 32px)" } : { width: 400 }]}
              >
                <Skeleton variant="rectangular" width={"100%"} height={"49%"} sx={{ mb: 1 }} />
                <Skeleton variant="rectangular" width={"100%"} height={"49%"} />
              </Box>
            )}
          </Grid2>
        </Fade>
      </LocationReportPopper>
    </Grid2>
  );
};

export default LocationCapacityMarker;
